import React, { useMemo, useState, useCallback, useEffect } from "react";
import { useAppContext } from "../AppContext";
import moment from "moment";
import { useTranslation } from 'react-i18next';
import LoadPage from "../common/LoadPage";
import useAxiosFetch from "../common/useAxiosFetch";
import axios from "axios";
import authHeader from "../services/auth-header";
import { API_BASE_URL } from "../common/config";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const ACTION_COLORS = {
  login: '#8884d8',
  create: '#00ca71',
  update: '#ffba00',
  delete: '#ff3245',
  import: '#8dd1e1',
  export: '#a4de6c',
  review: '#d0ed57',
  move: '#a28fd0',
  archive: '#9ca3af',
  restore: '#34d399',
  ai_advice: '#3b82f6',
  password_change: '#f59e0b',
  '2fa_change': '#ec4899',
};

const ENTITY_COLORS = [
  '#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#8dd1e1',
  '#a4de6c', '#d0ed57', '#a28fd0', '#3b82f6', '#f59e0b', '#ec4899',
];

const KPI_ICONS = {
  total: { icon: 'icon-statistics', className: '' },
  login: { icon: 'icon-login', className: '' },
  create: { icon: 'icon-add', className: 'kpi-low' },
  update: { icon: 'icon-edit', className: 'kpi-medium' },
  delete: { icon: 'icon-delete', className: 'kpi-high' },
  ai_advice: { icon: 'icon-stars1', className: '' },
};

const ActivityPage = () => {
  const { t } = useTranslation();

  // Tab: overview | feed
  const [activeTab, setActiveTab] = useState("overview");

  // Date range filter
  const [dayFilter, setDayFilter] = useState(30);

  // Over-time chart group mode
  const [overTimeGroupBy, setOverTimeGroupBy] = useState("action");

  // Feed state
  const [feedData, setFeedData] = useState(null);
  const [feedLoading, setFeedLoading] = useState(false);
  const [feedFilters, setFeedFilters] = useState({ action: '', entityType: '', limit: 50, offset: 0 });

  // Calculate date range
  const fromDate = useMemo(() => moment().subtract(dayFilter - 1, 'days').format('YYYY-MM-DD'), [dayFilter]);
  const toDate = useMemo(() => moment().format('YYYY-MM-DD'), [dayFilter]);

  // Fetch summary data
  const { data: summaryData, loading: summaryLoading } = useAxiosFetch(
    `/api/activities/summary?from=${fromDate}&to=${toDate}`
  );

  // Fetch over-time data
  const { data: overTimeData, loading: overTimeLoading } = useAxiosFetch(
    `/api/activities/over-time?from=${fromDate}&to=${toDate}&groupBy=${overTimeGroupBy}`
  );

  // Fetch feed data
  const fetchFeed = useCallback(async (filters = feedFilters) => {
    setFeedLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('from', fromDate);
      params.set('to', toDate);
      params.set('limit', filters.limit);
      params.set('offset', filters.offset);
      if (filters.action) params.set('action', filters.action);
      if (filters.entityType) params.set('entityType', filters.entityType);
      
      const res = await axios.get(`${API_BASE_URL}/api/activities/feed?${params.toString()}`, {
        headers: authHeader(),
      });
      setFeedData(res.data);
    } catch (err) {
      console.error('Error fetching activity feed:', err);
    }
    setFeedLoading(false);
  }, [fromDate, toDate, feedFilters]);

  useEffect(() => {
    if (activeTab === 'feed') {
      fetchFeed();
    }
  }, [activeTab, fetchFeed]);

  // Process over-time data into chart format
  const chartData = useMemo(() => {
    if (!overTimeData || !Array.isArray(overTimeData) || overTimeData.length === 0) return [];

    const endDay = moment(toDate);
    const startDay = moment(fromDate);

    // Build a map of days
    const daysMap = {};
    for (let m = moment(startDay); m.diff(endDay, 'days') <= 0; m.add(1, 'days')) {
      const currentDay = m.format('YYYY-MM-DD');
      daysMap[currentDay] = { day: currentDay };
    }

    // Fill in data
    overTimeData.forEach(item => {
      const { day, groupKey, count } = item;
      if (daysMap[day] && groupKey) {
        daysMap[day][groupKey] = (daysMap[day][groupKey] || 0) + count;
      }
    });

    return Object.values(daysMap).sort((a, b) => a.day.localeCompare(b.day));
  }, [overTimeData, fromDate, toDate]);

  // Extract dynamic keys from chart data
  const chartKeys = useMemo(() => {
    if (!overTimeData || !Array.isArray(overTimeData)) return [];
    const set = new Set();
    overTimeData.forEach(item => {
      if (item.groupKey) set.add(item.groupKey);
    });
    return Array.from(set);
  }, [overTimeData]);

  // Process summary for entity type pie chart
  const entityPieData = useMemo(() => {
    if (!summaryData?.byEntityType) return [];
    return summaryData.byEntityType
      .filter(item => item.entityType && item.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [summaryData]);

  // Get action count helper
  const getActionCount = (action) => {
    if (!summaryData?.byAction) return 0;
    const found = summaryData.byAction.find(a => a.action === action);
    return found?.count || 0;
  };

  const loading = summaryLoading || overTimeLoading;
  if (loading) return <LoadPage />;

  return (
    <div id="content">
      <div id="content-title">{t('activity_statistics.title')}</div>

      {/* Tab buttons */}
      <div
        className={`white-border-btn ${activeTab === 'overview' ? 'active' : ''}`}
        onClick={() => setActiveTab('overview')}
      >
        <i className="icon-pie" /> {t('activity_statistics.overview')}
      </div>
      <div
        className={`white-border-btn ${activeTab === 'feed' ? 'active' : ''}`}
        onClick={() => setActiveTab('feed')}
      >
        <i className="icon-list" /> {t('activity_statistics.activity_feed')}
      </div>

      {/* Day filter buttons */}
      {[7, 30, 60, 90].map((days) => (
        <div
          key={days}
          className={`white-border-btn ${dayFilter === days ? 'active' : ''}`}
          style={{ marginRight: 5 }}
          onClick={() => setDayFilter(days)}
        >
          {days} {t('days').toLowerCase()}
        </div>
      ))}

      <div className="clear-both" />

      {activeTab === 'overview' && (
        <>
          {/* KPI Cards */}
          <div className="content-bubble kpi-bubble">
            <div className="kpi-row">
              <div className="kpi-card">
                <i className={`${KPI_ICONS.total.icon} kpi-icon`} />
                <div className="bubble-title" style={{float: 'none'}}>{t('activity_statistics.total_activities')}</div>
                <div style={{ fontSize: 32, fontWeight: 'bold', color: 'var(--text-color)' }}>{summaryData?.total || 0}</div>
              </div>
              <div className="kpi-card">
                <i className={`${KPI_ICONS.login.icon} kpi-icon`} />
                <div className="bubble-title" style={{float: 'none'}}>{t('activity_statistics.logins')}</div>
                <div style={{ fontSize: 32, fontWeight: 'bold', color: '#8884d8' }}>{getActionCount('login')}</div>
              </div>
              <div className="kpi-card">
                <i className={`${KPI_ICONS.create.icon} kpi-icon ${KPI_ICONS.create.className}`} />
                <div className="bubble-title" style={{float: 'none'}}>{t('activity_statistics.creates')}</div>
                <div style={{ fontSize: 32, fontWeight: 'bold', color: 'var(--green-risk-score)' }}>{getActionCount('create')}</div>
              </div>
              <div className="kpi-card">
                <i className={`${KPI_ICONS.update.icon} kpi-icon ${KPI_ICONS.update.className}`} />
                <div className="bubble-title" style={{float: 'none'}}>{t('activity_statistics.updates')}</div>
                <div style={{ fontSize: 32, fontWeight: 'bold', color: 'var(--yellow-risk-score)' }}>{getActionCount('update')}</div>
              </div>
              <div className="kpi-card">
                <i className={`${KPI_ICONS.delete.icon} kpi-icon ${KPI_ICONS.delete.className}`} />
                <div className="bubble-title" style={{float: 'none'}}>{t('activity_statistics.deletes')}</div>
                <div style={{ fontSize: 32, fontWeight: 'bold', color: 'var(--red-risk-score)' }}>{getActionCount('delete')}</div>
              </div>
              <div className="kpi-card">
                <i className={`${KPI_ICONS.ai_advice.icon} kpi-icon`} />
                <div className="bubble-title" style={{float: 'none'}}>{t('activity_statistics.ai_advice')}</div>
                <div style={{ fontSize: 32, fontWeight: 'bold', color: '#3b82f6' }}>{getActionCount('ai_advice')}</div>
              </div>
            </div>
          </div>

          {/* Activity Over Time Chart */}
          <div className="content-bubble">
            <div className="bubble-title">{t('activity_statistics.activity_over_time')}</div>
            <div style={{ float: 'right', marginBottom: 10 }}>
              {['action', 'entityType', 'environment'].map(mode => (
                <div
                  key={mode}
                  className={`white-border-btn ${overTimeGroupBy === mode ? 'active' : ''}`}
                  style={{ marginRight: 5, height: 36, lineHeight: '36px', fontSize: 14 }}
                  onClick={() => setOverTimeGroupBy(mode)}
                >
                  {t(`activity_statistics.group_by_${mode}`)}
                </div>
              ))}
            </div>
            <div className="clear-both" />
            <div className="chart-container" style={{ height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="day"
                    tickFormatter={(tick) => moment(tick).format("MM/DD")}
                    label={{ value: t('day'), position: "insideBottomRight", offset: 0 }}
                  />
                  <YAxis label={{ value: t('activity_statistics.count'), angle: -90, position: "insideLeft" }} />
                  <Tooltip labelFormatter={(label) => moment(label).format("YYYY-MM-DD")} />
                  <Legend />
                  {chartKeys.map((key, index) => {
                    const color = overTimeGroupBy === 'action'
                      ? (ACTION_COLORS[key] || ENTITY_COLORS[index % ENTITY_COLORS.length])
                      : ENTITY_COLORS[index % ENTITY_COLORS.length];
                    return (
                      <Bar
                        key={key}
                        dataKey={key}
                        fill={color}
                        name={t(`activity_statistics.${key}`, key)}
                        stackId="a"
                      />
                    );
                  })}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Bottom row: Entity breakdown + Top users + Environment breakdown */}
          <div className="activity-grid">
            {/* Entity Type Breakdown */}
            <div className="content-bubble">
              <div className="bubble-title">{t('activity_statistics.by_entity_type')}</div>
              <div className="clear-both" />
              <div style={{ height: '80%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={entityPieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ entityType, count, percent }) =>
                        `${t(`activity_statistics.${entityType}`, entityType)}: ${count} (${(percent * 100).toFixed(0)}%)`
                      }
                      outerRadius={100}
                      dataKey="count"
                      nameKey="entityType"
                    >
                      {entityPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={ENTITY_COLORS[index % ENTITY_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [value, t(`activity_statistics.${name}`, name)]} />
                    <Legend formatter={(value) => t(`activity_statistics.${value}`, value)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Top Users */}
            <div className="content-bubble">
              <div className="bubble-title">{t('activity_statistics.top_users')}</div>
              <div className="clear-both" />
              {summaryData?.topUsers && summaryData.topUsers.length > 0 ? (
                <div className="content-bubble content-bubble-table content-bubble-small">
                  <table className="overview-table">
                    <thead>
                      <tr>
                        <td>#</td>
                        <td>{t('user')}</td>
                        <td>{t('activity_statistics.actions')}</td>
                      </tr>
                    </thead>
                    <tbody>
                      {summaryData.topUsers.map((user, index) => (
                        <tr key={user.userId}>
                          <td>{index + 1}</td>
                          <td style={{ textAlign: 'left' }}>{user.userName || user.userId}</td>
                          <td><strong>{user.count}</strong></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--gray-text-color)' }}>
                  {t('activity_statistics.no_data')}
                </div>
              )}
            </div>
          </div>

          {/* Environment Breakdown */}
          {summaryData?.byEnvironment && summaryData.byEnvironment.length > 0 && (
            <div className="content-bubble">
              <div className="bubble-title">{t('activity_statistics.by_environment')}</div>
              <div className="clear-both" />
              <div className="content-bubble content-bubble-table content-bubble-small">
                <table className="overview-table">
                  <thead>
                    <tr>
                      <td>{t('environment')}</td>
                      <td>{t('activity_statistics.actions')}</td>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryData.byEnvironment.map((env) => (
                      <tr key={env.environmentId}>
                        <td style={{ textAlign: 'left' }}>{env.environmentId}</td>
                        <td><strong>{env.count}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === 'feed' && (
        <>
          {/* Feed Filters */}
          <div className="content-bubble" style={{ paddingBottom: 20 }}>
            <div className="bubble-title">{t('activity_statistics.filters')}</div>
            <div className="clear-both" />
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 14 }}>
                {t('activity_statistics.action_type')}
                <select
                  value={feedFilters.action}
                  onChange={(e) => setFeedFilters(prev => ({ ...prev, action: e.target.value, offset: 0 }))}
                  style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--shadow-color)', fontSize: 14 }}
                >
                  <option value="">{t('activity_statistics.all')}</option>
                  {['login', 'create', 'update', 'delete', 'import', 'review', 'move', 'archive', 'restore', 'ai_advice', 'password_change', '2fa_change', 'send_mailing'].map(action => (
                    <option key={action} value={action}>{t(`activity_statistics.${action}`, action)}</option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 14 }}>
                {t('activity_statistics.entity_type')}
                <select
                  value={feedFilters.entityType}
                  onChange={(e) => setFeedFilters(prev => ({ ...prev, entityType: e.target.value, offset: 0 }))}
                  style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--shadow-color)', fontSize: 14 }}
                >
                  <option value="">{t('activity_statistics.all')}</option>
                  {['risk', 'measure', 'organization', 'project', 'user', 'risk_template', 'environment', 'copilot', 'soa', 'session', 'mailing'].map(entity => (
                    <option key={entity} value={entity}>{t(`activity_statistics.${entity}`, entity)}</option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          {/* Feed Table */}
          <div className="content-bubble content-bubble-table">
            {feedLoading ? (
              <LoadPage />
            ) : feedData?.activities && feedData.activities.length > 0 ? (
              <>
                <table className="overview-table">
                  <thead>
                    <tr>
                      <td>{t('activity_statistics.timestamp')}</td>
                      <td>{t('user')}</td>
                      <td>{t('activity_statistics.action_type')}</td>
                      <td>{t('activity_statistics.entity_type')}</td>
                      <td>{t('activity_statistics.entity_name')}</td>
                      <td>{t('environment')}</td>
                    </tr>
                  </thead>
                  <tbody>
                    {feedData.activities.map((activity) => (
                      <tr key={activity.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {moment(activity.timestamp).format('YYYY-MM-DD HH:mm')}
                        </td>
                        <td style={{ textAlign: 'left' }}>{activity.userName || activity.userId}</td>
                        <td>
                          <span style={{
                            display: 'inline-block',
                            padding: '2px 10px',
                            borderRadius: 12,
                            fontSize: 13,
                            fontWeight: 600,
                            background: ACTION_COLORS[activity.action] || '#8884d8',
                            color: '#fff',
                          }}>
                            {t(`activity_statistics.${activity.action}`, activity.action)}
                          </span>
                        </td>
                        <td>{t(`activity_statistics.${activity.entityType}`, activity.entityType)}</td>
                        <td style={{ textAlign: 'left', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {activity.entityName || '-'}
                        </td>
                        <td>{activity.environmentId}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, padding: '0 8px' }}>
                  <span style={{ fontSize: 14, color: 'var(--gray-text-color)' }}>
                    {t('activity_statistics.showing')} {feedData.offset + 1}-{Math.min(feedData.offset + feedData.limit, feedData.total)} {t('activity_statistics.of')} {feedData.total}
                  </span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {feedData.offset > 0 && (
                      <div
                        className="pagination-btn"
                        onClick={() => {
                          const newFilters = { ...feedFilters, offset: Math.max(0, feedFilters.offset - feedFilters.limit) };
                          setFeedFilters(newFilters);
                          fetchFeed(newFilters);
                        }}
                      >
                        &laquo; {t('activity_statistics.previous')}
                      </div>
                    )}
                    {feedData.offset + feedData.limit < feedData.total && (
                      <div
                        className="pagination-btn"
                        onClick={() => {
                          const newFilters = { ...feedFilters, offset: feedFilters.offset + feedFilters.limit };
                          setFeedFilters(newFilters);
                          fetchFeed(newFilters);
                        }}
                      >
                        {t('activity_statistics.next')} &raquo;
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: 60, color: 'var(--gray-text-color)' }}>
                <i className="icon-inbox" style={{ fontSize: 48, marginBottom: 16, display: 'block' }} />
                {t('activity_statistics.no_data')}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ActivityPage;
